import React from "react";
import { Text } from "@telia/teddy";
import moment, { Moment } from "moment";
import { useSelector } from "react-redux";
import { CUSTOMER_TYPE } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { getPortingDates } from "../../../state/selectors/cartSelectors";
import { getSubscriptions } from "../../../state/selectors/subscriptionsSelectors";
import { PhoneNumberFormatters } from "../../../../app/utils";
import "./PortingDetailsCard.less";

interface PortingEntry {
    phoneNumber: string;
    name: string;
    isEsim: boolean;
    date: Moment;
}

const formatEntry = (entry: PortingEntry): React.ReactNode => (
    <Text className="porting-details-card__line">
        <Text as="span" variant="paragraph-100-bold" mr="50">
            Tlf. {PhoneNumberFormatters[0].displayFn(entry.phoneNumber)}:
        </Text>
        {`${entry.date.format("DD.MM.YYYY")} kl. ${entry.date.format("HH:mm")} - ${entry.name}`}
    </Text>
);

const EntryList: React.FC<{ entries: PortingEntry[] }> = ({ entries }) => (
    <ul className="porting-details-card__list">
        {entries.map((entry) => (
            <li key={entry.phoneNumber} data-di-mask>
                {formatEntry(entry)}
            </li>
        ))}
    </ul>
);

const PortingDetailsCard: React.FC = () => {
    const portingDates = useSelector((state: RootState) => getPortingDates(state));
    const subscriptions = useSelector((state: RootState) => getSubscriptions(state));

    const entries: PortingEntry[] = Object.values(subscriptions).flatMap((sub) => {
        if (sub.phoneNumber?.check?.type !== CUSTOMER_TYPE.PORT_IN) return [];
        const phoneNumber = sub.phoneNumber?.selectedPhoneNumber;
        if (!phoneNumber || !portingDates[phoneNumber]) return [];
        return [
            {
                phoneNumber,
                name: sub.name,
                isEsim: sub.isEsimSelected,
                date: moment(portingDates[phoneNumber]),
            },
        ];
    });

    if (entries.length === 0) return null;

    const esimEntries = entries.filter((e) => e.isEsim);
    const physicalEntries = entries.filter((e) => !e.isEsim);
    const hasMixed = esimEntries.length > 0 && physicalEntries.length > 0;

    const renderEntries = () => {
        if (entries.length === 1) {
            return <p data-di-mask>{formatEntry(entries[0])}</p>;
        }
        if (hasMixed) {
            return (
                <>
                    <Text className="porting-details-card__subheading">eSIM</Text>
                    <EntryList entries={esimEntries} />
                    <Text className="porting-details-card__subheading">Fysisk SIM</Text>
                    <EntryList entries={physicalEntries} />
                </>
            );
        }
        return <EntryList entries={entries} />;
    };

    const smsNotice = entries.length === 1 ? "Du får SMS når det skjer" : "Du får SMS når hvert abonnement aktiveres";

    return (
        <div className="porting-details-card">
            {renderEntries()}
            <Text style={{ fontStyle: "italic" }}>{smsNotice}</Text>
        </div>
    );
};

export default PortingDetailsCard;
